import api from './api';
import { ApiResponse } from '../types';

export interface ImpersonationGrant {
  id: string; targetType: 'merchant' | 'merchant_customers' | 'customer'; targetId: string;
  reason: string | null; grantedAt: string; expiresAt: string | null; revokedAt: string | null; active: boolean;
}

/**
 * B50 / N1 — an impersonation is issued an ACCESS token only. It used to carry a
 * 30-day refresh token, and when the impersonation ended the interceptor
 * refreshed it into a plain session as the target. `refreshToken` stays in the
 * type only so a response from a pre-B50 server still type-checks; it is never
 * stored.
 */
export interface ImpersonationTokens { accessToken: string; refreshToken?: string }

export const impersonationApi = {
  listGrants: () => api.get<ApiResponse<{ grants: ImpersonationGrant[] }>>('/impersonation/grants'),
  // durationHours: 24 | 168 (7d). untilRevoked: true ⇒ standing grant (no expiry).
  createGrant: (d: { targetType: string; durationHours?: number; untilRevoked?: boolean; customerId?: string; reason?: string }) =>
    api.post<ApiResponse<{ grant: { id: string; expiresAt: string | null } }>>('/impersonation/grants', d),
  revokeGrant: (id: string) => api.delete<ApiResponse>(`/impersonation/grants/${id}`),
  start: (targetUserId: string) =>
    api.post<ApiResponse<{ user: any; tokens: ImpersonationTokens; sessionId: string; impersonatedBy: string; lane: string }>>('/impersonation/start', { targetUserId }),
  end: (sessionId: string) => api.post<ApiResponse>('/impersonation/end', { sessionId }),
  sessions: () => api.get<ApiResponse<{ sessions: any[] }>>('/impersonation/sessions'),
  // Admin: active grants → consented (normal) vs no-consent (break-glass) per user.
  activeGrants: () => api.get<ApiResponse<{ grants: { targetType: string; targetId: string }[] }>>('/impersonation/active-grants'),
};

// ── Token-swap helpers (per-tab via sessionStorage) ──
const RK = 'impersonation:return';

export const isImpersonating = (): boolean => !!sessionStorage.getItem(`${RK}:token`);
export const impersonatedName = (): string => sessionStorage.getItem(`${RK}:targetName`) || 'another user';

/**
 * Wipe all impersonation return-state from sessionStorage. Call on logout/login
 * so an impersonation started in one session can't leak into the next one in the
 * same tab (which otherwise leaves isImpersonating() true after a re-login).
 */
export const clearImpersonationState = (): void => {
  sessionStorage.removeItem(`${RK}:token`);
  sessionStorage.removeItem(`${RK}:refreshToken`);
  sessionStorage.removeItem(`${RK}:sessionId`);
  sessionStorage.removeItem(`${RK}:targetName`);
};

/**
 * Enter an impersonation session whose tokens were obtained from a DIFFERENT
 * endpoint than /impersonation/start — e.g. the admin Users-page button
 * (POST /users/:id/impersonate). Mirrors startImpersonation's sessionStorage
 * stashing so the ImpersonationBanner shows and "Exit" restores the
 * impersonator. Caller handles navigation/reload.
 */
export function enterImpersonation(opts: { accessToken: string; sessionId: string; targetName: string }): void {
  sessionStorage.setItem(`${RK}:token`, localStorage.getItem('token') || '');
  sessionStorage.setItem(`${RK}:refreshToken`, localStorage.getItem('refreshToken') || '');
  sessionStorage.setItem(`${RK}:sessionId`, opts.sessionId);
  sessionStorage.setItem(`${RK}:targetName`, opts.targetName);
  localStorage.setItem('token', opts.accessToken);
  // No refresh token while impersonating (B50). REMOVE the impersonator's — never
  // `setItem(…, undefined)`, which stores the string "undefined" and sends it.
  localStorage.removeItem('refreshToken');
  // Drop any cart token from the previous identity — it belongs to a different
  // user/session and would 403 (CART_ACCESS_DENIED) in the impersonated Store.
  localStorage.removeItem('recurr_cart_token');
}

/** Begin impersonating targetUserId: stash current tokens, swap to the target, reload. */
export async function startImpersonation(targetUserId: string, targetName?: string): Promise<void> {
  const res = await impersonationApi.start(targetUserId);
  if (!res.data.success || !res.data.data) throw new Error(res.data.error?.message || 'Failed');
  const { tokens, sessionId } = res.data.data;
  // Stash the CURRENT (impersonator) session and swap to the target — the same
  // steps as enterImpersonation, so the two entry points cannot drift apart.
  enterImpersonation({ accessToken: tokens.accessToken, sessionId, targetName: targetName || '' });
  window.location.href = '/dashboard';
}

/**
 * Exit impersonation: end the audited session, restore the original tokens, reload.
 *
 * ⚠ SINGLE-FLIGHT, AND THE INTERCEPTOR MUST NOT AWAIT IT (B50). The interceptor
 * calls this on ANY 401 while impersonating — and when the session has already
 * ended, the `/impersonation/end` call below 401s too, which lands back in the
 * interceptor while this is still running. A second call returns the one in
 * flight; if the interceptor awaited it, the inner call would wait on the outer
 * one waiting on it.
 */
let exiting: Promise<void> | null = null;

export function exitImpersonation(): Promise<void> {
  if (!exiting) {
    exiting = doExitImpersonation().finally(() => { exiting = null; });
  }
  return exiting;
}

async function doExitImpersonation(): Promise<void> {
  const sessionId = sessionStorage.getItem(`${RK}:sessionId`);
  const retToken = sessionStorage.getItem(`${RK}:token`);
  const retRefresh = sessionStorage.getItem(`${RK}:refreshToken`);
  try { if (sessionId) await impersonationApi.end(sessionId); } catch { /* best-effort */ }
  if (retToken) localStorage.setItem('token', retToken);
  if (retRefresh) localStorage.setItem('refreshToken', retRefresh);
  else localStorage.removeItem('refreshToken');
  sessionStorage.removeItem(`${RK}:token`);
  sessionStorage.removeItem(`${RK}:refreshToken`);
  sessionStorage.removeItem(`${RK}:sessionId`);
  sessionStorage.removeItem(`${RK}:targetName`);
  localStorage.removeItem('recurr_cart_token');   // see enterImpersonation
  window.location.href = '/dashboard';
}
